import React, { useEffect } from 'react';
import {
  ATMHeader,
  ATMSegment,
  ATMGrid,
  ATMField,
  ATMDatePicker,
  ATMForm,
} from 'shared-it-appmod-ui';
import { REGEX_HOUR_MINUTE } from 'src/constants';
import Moment from 'src/libraries/moment.library';
import Lang from 'src/libraries/language';
import { INewFireDispatchMethods } from 'src/models/fire-fighting-dispatch.model';
import styles from './dispatch-information-form.module.scss';

type IProps = INewFireDispatchMethods;

const calcResponseTime = (timeData): string => {
  const dispatchTimeData: string[] =
    REGEX_HOUR_MINUTE.exec(timeData.dispatchTime) || [];
  const liftOffTimeData: string[] =
    REGEX_HOUR_MINUTE.exec(timeData.liftoffTime) || [];
  if (dispatchTimeData?.length > 2 && liftOffTimeData?.length > 2) {
    const dispatchHHMM = Moment(
      `${dispatchTimeData[1]}:${dispatchTimeData[2]}:00`,
      'HH:mm:ss'
    );
    const liftOffHHMM = Moment(
      `${liftOffTimeData[1]}:${liftOffTimeData[2]}:00`,
      'HH:mm:ss'
    );
    const diff = liftOffHHMM.diff(dispatchHHMM);
    return Moment.utc(diff).format('H:mm');
  }
  return '';
};

const DispatchInformationForm: React.FC<IProps> = ({ control, errors }) => {
  const dispatchTimeValue = control.watchInternal([
    'dispatchTime',
    'liftoffTime',
    'returnTime',
  ]);

  useEffect(() => {
    control.setValue('responseTime', calcResponseTime(dispatchTimeValue));
  }, [dispatchTimeValue]);

  return (
    <ATMSegment>
      <ATMHeader>{Lang.TTL_FIRE_DISPATCH_FORM_INFORMATION}</ATMHeader>
      <ATMGrid columns="4">
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name="dispatchDate"
              label={Lang.LBL_FIRE_DISPATCH_DISPATCH_DATE}
              format="MM/DD/YYYY"
              className={styles.datePicker}
              as={ATMDatePicker}
              control={control}
              error={errors.dispatchDate}
              onChange={([_, { value }]) => value}
              minDate={Moment().startOf('day').toDate()}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="dispatchTime"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_DISPATCH_TIME}
              type="number"
              as={ATMForm.Input}
              placeholder={Lang.LBL_TIME_SELECT}
              control={control}
              icon="clock outline"
              error={errors.dispatchTime}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="liftoffTime"
              type="number"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_LIFT_OFF_TIME}
              as={ATMForm.Input}
              placeholder={Lang.LBL_TIME_SELECT}
              control={control}
              icon="clock outline"
              error={errors.liftoffTime}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              readOnly
              name="responseTime"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_RESPONSE_TIME}
              placeholder="--"
              as={ATMForm.Input}
              control={control}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name="returnTime"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_RETURN_TIME}
              type="number"
              as={ATMForm.Input}
              placeholder={Lang.LBL_TIME_SELECT}
              control={control}
              icon="clock outline"
              error={errors.returnTime}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="flightHours"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_FLIGHT_HOURS}
              type="number"
              placeholder={Lang.LBL_HOURS_SELECT}
              as={ATMForm.Input}
              control={control}
              error={errors.flightHours}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name="location"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_LOCATION}
              as={ATMForm.Input}
              placeholder={Lang.LBL_ENTER_DETAILS}
              error={errors.location}
              control={control}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="lattitude"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_LATITUDE}
              as={ATMForm.Input}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.lattitude}
              type="number"
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="longitude"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_LONGITUDE}
              as={ATMForm.Input}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.longitude}
              type="number"
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>
    </ATMSegment>
  );
};

export default DispatchInformationForm;
